import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Icon from '../docs/Icon'
import Editable from '../inspector/Editable'
import AgencyMark from '../marks/AgencyMark'
import MissionPicker from '../missions/MissionPicker'
import { america, agencies, findMission, videoFor } from '@/content/missions'
import { heroPhotos, heroVideo, type HeroPhoto, type HeroVideo } from '@/content/media'
import { sansFonts } from '@/content/fonts'
import WavingFlag from '../marks/WavingFlag'
import { usePrefs } from '@/state/prefs'
import { useReducedMotion } from '@/state/useReducedMotion'
import { useBrand } from '@/state/useBrand'
import { useCasing } from '@/state/useCasing'
import { useSurprise } from '@/state/useSurprise'
import { textSizeGroup,
  displayFontOptions,
  fontDetail,
  heroOptions,
  creditOptions,
  linkStyleOptions,
  promptStyleOptions,
  sansFontOptions,
  markOptionsFor,
  radiusOptions,
  casingOptions,
} from './options'



function PauseButton({ paused, onToggle, what }: { paused: boolean; onToggle: () => void; what: string }) {
  return (
    <button
      type="button"
      className="home-hero__pause"
      aria-pressed={paused}
      aria-label={paused ? `Play background ${what}` : `Pause background ${what}`}
      onClick={onToggle}
    >
      <Icon name={paused ? 'play' : 'pause'} size={14} />
    </button>
  )
}

/** The media credit, as a caption line or behind a small badge. */
function Credit({ label, title, creator, source }: { label: string; title: string; creator: string; source: string }) {
  const { prefs, set } = usePrefs()
  const [open, setOpen] = useState(false)
  const text = (
    <>
      {label}:{' '}
      <a href={source}>
        {title}, {creator}
      </a>
    </>
  )
  return (
    <Editable
      label="Credit"
      value={prefs.credit}
      options={creditOptions}
      onChange={(value) => set('credit', value)}
      token="credit"
      pin="right"
      className="home-hero__credit-wrap"
    >
      {prefs.credit === 'badge' ? (
        <span className="home-hero__credit-badge">
          <button
            type="button"
            className="home-hero__credit-toggle"
            aria-expanded={open}
            aria-label={`${label} credit`}
            onClick={() => setOpen((state) => !state)}
          >
            <Icon name="info" size={14} />
          </button>
          {open && <span className="home-hero__credit home-hero__credit--floating">{text}</span>}
        </span>
      ) : (
        <p className="home-hero__credit">{text}</p>
      )}
    </Editable>
  )
}

/** Still photographs that crossfade every few seconds. */
function PhotoMedia({ reduced, photos }: { reduced: boolean; photos: HeroPhoto[] }) {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  // The furthest photo allowed to load: the first loads with the page, and
  // each next one a few seconds before it fades in.
  const [reach, setReach] = useState(0)
  const moving = !reduced && !paused && photos.length > 1

  useEffect(() => {
    if (!moving) return
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % photos.length), 7000)
    return () => window.clearInterval(timer)
  }, [moving, photos.length])

  useEffect(() => {
    if (!moving) return
    const timer = window.setTimeout(() => setReach((r) => Math.max(r, index + 1)), 2500)
    return () => window.clearTimeout(timer)
  }, [moving, index])

  if (photos.length === 0) return null
  const photo = photos[index]
  return (
    <>
      <div className="home-hero__media" aria-hidden="true">
        {photos.map((item, i) => (
          <img
            key={item.file}
            src={i <= reach || i === index ? item.small : undefined}
            srcSet={i <= reach || i === index ? `${item.small} 900w, ${item.file} 2000w` : undefined}
            sizes="100vw"
            alt=""
            className={i === index ? 'is-active' : ''}
            loading={i === 0 ? 'eager' : 'lazy'}
            decoding="async"
          />
        ))}
      </div>
      <Credit label="Photograph" title={photo.title} creator={photo.creator} source={photo.source} />
      {photos.length > 1 && !reduced && (
        <PauseButton paused={paused} onToggle={() => setPaused((state) => !state)} what="photographs" />
      )}
    </>
  )
}

/** A muted public-domain loop; a still poster under reduced motion. */
function VideoMedia({ reduced, video: clip, photos }: { reduced: boolean; video: HeroVideo | null; photos: HeroPhoto[] }) {
  const video = useRef<HTMLVideoElement>(null)
  const [paused, setPaused] = useState(false)
  useEffect(() => {
    const el = video.current
    if (!el) return
    if (paused || reduced) el.pause()
    else void el.play().catch(() => setPaused(true))
  }, [paused, reduced])

  if (!clip) return <PhotoMedia reduced={reduced} photos={photos} />
  return (
    <>
      <div className="home-hero__media home-hero__media--video" aria-hidden="true">
        {reduced ? (
          <img src={clip.poster} alt="" className="is-active" decoding="async" />
        ) : (
          <video ref={video} className="is-active" muted loop playsInline autoPlay poster={clip.poster} preload="metadata">
            <source src={clip.webm} type="video/webm" />
            <source src={clip.mp4} type="video/mp4" />
          </video>
        )}
      </div>
      <Credit label="Video" title={clip.title} creator={clip.creator} source={clip.source} />
      {!reduced && <PauseButton paused={paused} onToggle={() => setPaused((state) => !state)} what="video" />}
    </>
  )
}

/** The generated waving flag; the static flag under reduced motion. */
function FlagMedia({ reduced }: { reduced: boolean }) {
  const [paused, setPaused] = useState(false)
  return (
    <>
      <div className="home-hero__media home-hero__media--flag" aria-hidden="true">
        <WavingFlag paused={paused || reduced} className="is-active" />
      </div>
      {!reduced && <PauseButton paused={paused} onToggle={() => setPaused((state) => !state)} what="flag" />}
    </>
  )
}

/** Background media follows the mission: its own photos and video, or America's. */
function HeroMedia() {
  const { prefs } = usePrefs()
  const reduced = useReducedMotion()
  const mission = findMission(prefs.mission) ?? america
  const photos = mission.media?.photos ?? heroPhotos
  const video = videoFor(mission, heroVideo)
  if (prefs.hero === 'none') return null
  if (prefs.hero === 'video') return <VideoMedia key={mission.id} reduced={reduced} video={video} photos={photos} />
  if (prefs.hero === 'flag') return <FlagMedia reduced={reduced} />
  return <PhotoMedia key={mission.id} reduced={reduced} photos={photos} />
}

/** Cycles example agencies in the pill, like a search field's suggestion. */
function useExample() {
  const reduced = useReducedMotion()
  const [index, setIndex] = useState(0)
  useEffect(() => {
    if (reduced || agencies.length < 2) return
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % agencies.length), 3200)
    return () => window.clearInterval(timer)
  }, [reduced])
  return agencies[index] ?? america
}

function linkClass(style: string, first: boolean) {
  if (style === 'solid') return first ? 'mds-button--primary' : 'mds-button--secondary'
  if (style === 'outline') return 'mds-button--secondary'
  return 'mds-button--ghost'
}

export default function HomeHero() {
  const { prefs, set } = usePrefs()
  const [pickerOpen, setPickerOpen] = useState(false)
  const { surprise, announcement } = useSurprise()
  const mission = findMission(prefs.mission) ?? america
  const example = useExample()
  const chosen = mission.id !== america.id
  const { place } = useBrand()
  const cased = useCasing()
  const headline = `Design for ${place}.`

  return (
    <section className="home-hero" aria-labelledby="home-title">
      <Editable
        label="Hero media"
        value={prefs.hero}
        options={heroOptions}
        onChange={(value) => set('hero', value)}
        token="hero.media"
        pin="inside-bottom-left"
        className="home-hero__canvas mds-editable--region"
      >
        <HeroMedia />
        <div className="home-hero__content">
          <Editable
            label="Mark"
            value={prefs.mark}
            options={markOptionsFor(mission)}
            onChange={(value) => set('mark', value)}
            token="data-mark"
            pin="right"
            detail={chosen ? `${mission.name}: seal, or an original emblem from its ${mission.motif}.` : 'The flag of the United States.'}
            className="home-hero__mark-wrap home-enter"
          >
            <AgencyMark mission={mission} variant={prefs.mark} className="home-hero__mark" title={chosen ? mission.name : 'Flag of the United States'} />
          </Editable>
          <Editable
            label="Headline font"
            value={prefs.fontDisplay}
            options={displayFontOptions}
            onChange={(value) => set('fontDisplay', value)}
            token="--mds-font-display"
            detail={fontDetail(prefs.fontDisplay)}
            className="home-enter"
            more={[
              {
                label: 'Case',
                value: prefs.casing,
                options: casingOptions,
                onChange: (value) => set('casing', value as typeof prefs.casing),
              },
            ]}
            optionsLabel="Font"
          >
            <h1
              id="home-title"
              className="mds-hero-title home-hero__title"
              style={{ '--home-title-fit': Math.max(0.6, Math.min(1, 22 / headline.length)) } as React.CSSProperties}
            >
              {headline}
            </h1>
          </Editable>
          <Editable
            label="Body font"
            value={prefs.fontSans}
            options={sansFontOptions}
            onChange={(value) => set('fontSans', value)}
            token="--mds-font-sans"
            optionsLabel="Font"
            more={[textSizeGroup(prefs.textSize, (value) => set('textSize', value))]}
            detail={fontDetail(prefs.fontSans, sansFonts)}
            pin="right"
            className="home-enter"
          >
            <p className="home-hero__lead">
              The design system for United States government websites.
              <br />
              {chosen ? 'Choose another mission.' : 'Choose your mission.'}
            </p>
          </Editable>
          <Editable
            label="Shape"
            value={prefs.radius}
            options={radiusOptions}
            onChange={(value) => set('radius', value)}
            token="--mds-radius-control"
            className="home-hero__prompt-wrap home-enter"
            pin="right"
            more={[
              {
                label: 'Pill style',
                value: prefs.prompt,
                options: promptStyleOptions,
                onChange: (value) => set('prompt', value as typeof prefs.prompt),
              },
            ]}
          >
            <button type="button" className={`mds-prompt home-hero__prompt home-hero__prompt--${prefs.prompt}`} onClick={() => setPickerOpen(true)}
              aria-haspopup="dialog"
            >
              <span className="home-hero__prompt-mark" aria-hidden="true">
                <AgencyMark mission={chosen ? mission : example} variant={prefs.mark === 'motion' ? 'flag' : prefs.mark} />
              </span>
              <span className="home-hero__prompt-text">
                <span className="home-hero__prompt-label">{chosen ? mission.name : cased('Choose your mission')}</span>{' '}
                {/* With a mission chosen the hint is part of the name, "Department of War Change mission",
                    so voice users can say what they see; the example agency stays out of it. */}
                <span className="home-hero__prompt-hint" aria-hidden={chosen ? undefined : true}>
                  {chosen ? cased('Change mission') : `Try ‘${example.name}’`}
                </span>
              </span>
              <span className="mds-prompt__send" aria-hidden="true">
                <Icon name="arrow" />
              </span>
            </button>
          </Editable>
          <Editable
            label="Link style"
            value={prefs.heroLinks}
            options={linkStyleOptions}
            onChange={(value) => set('heroLinks', value)}
            token="mds-button"
            pin="right"
            className="home-enter"
          >
            <div className="home-hero__links">
              <Link to="/getting-started" className={`mds-button ${linkClass(prefs.heroLinks, true)}`}>
                {cased('Start building')} <Icon name="arrow" size={16} />
              </Link>
              <Link to="/components" className={`mds-button ${linkClass(prefs.heroLinks, false)}`}>
                {cased('Explore the components')} <Icon name="arrow" size={16} />
              </Link>
              <button type="button" className={`mds-button ${linkClass(prefs.heroLinks, false)}`} onClick={surprise}>
                <Icon name="spark" size={16} /> {cased('Surprise me')}
              </button>
            </div>
          </Editable>
          <p className="mds-visually-hidden" role="status">
            {announcement}
          </p>
        </div>
      </Editable>
      <MissionPicker open={pickerOpen} onClose={() => setPickerOpen(false)} />
    </section>
  )
}
